/**
 * Registry central de tipos de anúncio.
 *
 * Este arquivo é a única fonte de verdade para:
 * - labels/descrições por tipo
 * - mapeamento adType → postType
 * - campos disponíveis por tipo
 * - ordenação/padrões de exibição
 *
 * Admin/futuro: os defaults abaixo podem ser sobrepostos por config
 * vinda do Firestore/admin sem alterar código.
 */

export const AD_TYPES = {
  wanted: {
    value: 'wanted',
    label: 'Estou procurando por',
    categoryLabel: 'Quero Comprar',
    badgeLabel: 'Quero Comprar',
    sublabel: 'Peça o que precisa',
    description: 'Anúncio de demanda / Estou Procurando.',
    icon: '🔍',
    postType: 'need',
    order: 1,
    enabled: true,
    priceTypeOptions: [{ value: 'contact', label: 'Contate para preço' }],
    fields: {
      budgetMin: { label: 'Orçamento mínimo', required: false, visible: true },
      budgetMax: { label: 'Orçamento máximo', required: false, visible: true },
      urgency: { label: 'Urgência', required: false, visible: true },
      desiredCondition: {
        label: 'Estado desejado',
        required: false,
        visible: true,
      },
      deadline: { label: 'Prazo desejado', required: false, visible: true },
      referenceImageUrl: {
        label: 'Foto de referência',
        required: false,
        visible: true,
      },
      condition: {
        label: 'Aceita qualquer condição',
        required: false,
        visible: true,
      },
      contactPreference: { label: 'Contato', required: false, visible: true },
      location: { label: 'Localização', required: true, visible: true },
    },
  },
  for_sale: {
    value: 'for_sale',
    label: 'Tenho para oferecer',
    categoryLabel: 'À Venda',
    badgeLabel: 'À Venda',
    sublabel: 'Venda seu item',
    description: 'Anúncio de venda de produto ou serviço.',
    icon: '💲',
    postType: 'have',
    order: 2,
    enabled: true,
    priceTypeOptions: [
      { value: 'fixed', label: 'Preço Fixo' },
      { value: 'negotiable', label: 'Negociável' },
    ],
    fields: {
      price: { label: 'Preço', required: true, visible: true },
      priceType: { label: 'Tipo de preço', required: true, visible: true },
      negotiable: { label: 'Preço negociável', required: false, visible: true },
      condition: { label: 'Condição', required: true, visible: true },
      listedBy: { label: 'Anunciado por', required: false, visible: true },
      delivery: { label: 'Entrega', required: false, visible: true },
      warranty: { label: 'Garantia', required: false, visible: true },
      returnPolicy: {
        label: 'Política de devolução',
        required: false,
        visible: true,
      },
      paymentMethods: {
        label: 'Formas de pagamento',
        required: false,
        visible: true,
      },
      contactPreference: { label: 'Contato', required: false, visible: true },
    },
  },
  for_rent: {
    value: 'for_rent',
    label: 'Para alugar',
    categoryLabel: 'Aluguel',
    badgeLabel: 'Aluguel',
    sublabel: 'Alugue seu item',
    description: 'Anúncio de aluguel de produto ou imóvel.',
    icon: '📅',
    postType: 'have',
    order: 3,
    enabled: true,
    priceTypeOptions: [
      { value: 'fixed', label: 'Valor Fixo' },
      { value: 'negotiable', label: 'Negociável' },
    ],
    fields: {
      price: { label: 'Valor do aluguel', required: true, visible: true },
      priceType: { label: 'Tipo de preço', required: true, visible: true },
      rentalPeriod: { label: 'Período', required: true, visible: true },
      deposit: { label: 'Caução', required: false, visible: true },
      leaseType: { label: 'Tipo de contrato', required: false, visible: true },
      condition: { label: 'Condição', required: true, visible: true },
      listedBy: { label: 'Anunciado por', required: false, visible: true },
      delivery: { label: 'Entrega/retirada', required: false, visible: true },
      warranty: { label: 'Garantia', required: false, visible: true },
      contactPreference: { label: 'Contato', required: false, visible: true },
    },
  },
  exchange: {
    value: 'exchange',
    label: 'Quero trocar',
    categoryLabel: 'Troca',
    badgeLabel: 'Troca',
    sublabel: 'Proponha uma troca',
    description: 'Anúncio de troca por outro item.',
    icon: '🔄',
    postType: 'have',
    order: 4,
    enabled: true,
    priceTypeOptions: [
      { value: 'fixed', label: 'Valor de referência' },
      { value: 'negotiable', label: 'Negociável' },
    ],
    fields: {
      price: { label: 'Valor de referência', required: false, visible: true },
      exchangeDescription: {
        label: 'Descrição da troca',
        required: true,
        visible: true,
      },
      desiredItems: {
        label: 'Itens desejados',
        required: false,
        visible: true,
      },
      condition: { label: 'Condição', required: true, visible: true },
      listedBy: { label: 'Anunciado por', required: false, visible: true },
      location: { label: 'Localização', required: true, visible: true },
      contactPreference: { label: 'Contato', required: false, visible: true },
    },
  },
  free: {
    value: 'free',
    label: 'Quero doar',
    categoryLabel: 'Doação',
    badgeLabel: 'Doação',
    sublabel: 'Doe um item',
    description: 'Anúncio de doação de item.',
    icon: '🎁',
    postType: 'have',
    order: 5,
    enabled: true,
    priceTypeOptions: [],
    fields: {
      reason: { label: 'Motivo da doação', required: false, visible: true },
      condition: { label: 'Condição', required: true, visible: true },
      pickupOnly: { label: 'Somente retirada', required: false, visible: true },
      listedBy: { label: 'Anunciado por', required: false, visible: true },
      location: { label: 'Localização', required: true, visible: true },
      contactPreference: { label: 'Contato', required: false, visible: true },
    },
  },
};

export const AD_TYPE_OPTIONS = Object.values(AD_TYPES).filter((t) => t.enabled);

export const POST_TYPE_OPTIONS = [
  { value: 'have', label: 'Tenho' },
  { value: 'need', label: 'Preciso' },
];

export const AD_TYPE_LIST = [
  {
    value: 'wanted',
    label: 'Estou procurando por',
    categoryLabel: 'Quero Comprar',
    badgeLabel: 'Quero Comprar',
    sublabel: 'Peça o que precisa',
  },
  {
    value: 'for_sale',
    label: 'Tenho para oferecer',
    categoryLabel: 'À Venda',
    badgeLabel: 'À Venda',
    sublabel: 'Venda seu item',
  },
  {
    value: 'for_rent',
    label: 'Para alugar',
    categoryLabel: 'Aluguel',
    badgeLabel: 'Aluguel',
    sublabel: 'Alugue seu item',
  },
  {
    value: 'exchange',
    label: 'Quero trocar',
    categoryLabel: 'Troca',
    badgeLabel: 'Troca',
    sublabel: 'Proponha uma troca',
  },
  {
    value: 'free',
    label: 'Quero doar',
    categoryLabel: 'Doação',
    badgeLabel: 'Doação',
    sublabel: 'Doe um item',
  },
];

export function getAdTypeByValue(value) {
  return AD_TYPES[value] || null;
}

export function getPostTypeForAdType(adType) {
  const type = AD_TYPES[adType];
  return type ? type.postType : 'have';
}

export function getAdTypeFields(adType) {
  const type = AD_TYPES[adType];
  if (!type) return [];
  return Object.entries(type.fields)
    .map(([key, config]) => ({
      key,
      label: config.label,
      required: config.required,
      visible: config.visible,
    }))
    .sort((a, b) => a.key.localeCompare(b.key));
}

export function getPriceTypeOptions(adType) {
  const type = AD_TYPES[adType];
  if (!type || !Array.isArray(type.priceTypeOptions)) return [];
  return type.priceTypeOptions;
}

export function getEnabledAdTypes() {
  return Object.values(AD_TYPES)
    .filter((t) => t.enabled)
    .sort((a, b) => a.order - b.order);
}

export function isNeedAd(adType) {
  return getPostTypeForAdType(adType) === 'need';
}
